import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';

const WARNING_DURATION = 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];
const THROTTLE_MS = 10000;

export default function SessionTimeout() {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const { logout, tenant } = useAuthStore();
  const idleTimer = useRef(null);
  const warningTimer = useRef(null);
  const countdownInterval = useRef(null);
  const lastActivity = useRef(Date.now());
  const isWarningActive = useRef(false);

  // Read timeout from tenant settings (default 30 min)
  const timeoutMinutes = tenant?.settings?.session_timeout_minutes || 30;
  const IDLE_TIMEOUT = timeoutMinutes * 60 * 1000;

  const doLogout = useCallback(() => {
    clearAllTimers();
    isWarningActive.current = false;
    setShowWarning(false);
    logout();
    window.location.href = '/login?reason=timeout';
  }, [logout]);

  const clearAllTimers = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
  };

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setCountdown(60);

    idleTimer.current = setTimeout(() => {
      isWarningActive.current = true;
      setShowWarning(true);
      setCountdown(60);

      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            doLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      warningTimer.current = setTimeout(doLogout, WARNING_DURATION);
    }, IDLE_TIMEOUT);
  }, [doLogout, IDLE_TIMEOUT]);

  const handleActivity = useCallback(() => {
    // Ignore activity events while warning modal is active
    if (isWarningActive.current) return;

    const now = Date.now();
    if (now - lastActivity.current < THROTTLE_MS) return;
    lastActivity.current = now;

    startIdleTimer();
  }, [startIdleTimer]);

  const handleStayLoggedIn = () => {
    isWarningActive.current = false;
    lastActivity.current = Date.now();
    startIdleTimer();
  };

  useEffect(() => {
    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, handleActivity));
    startIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, handleActivity));
      clearAllTimers();
    };
  }, [handleActivity, startIdleTimer]);

  useEffect(() => {
    const interceptor = (event) => {
      if (event.detail?.status === 440) {
        doLogout();
      }
    };
    window.addEventListener('session-timeout', interceptor);
    return () => window.removeEventListener('session-timeout', interceptor);
  }, [doLogout]);

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Session Expiring</h3>
        <p className="text-gray-600 mb-4">
          You&apos;ve been inactive for {timeoutMinutes} minutes. Your session will expire in{' '}
          <span className="font-bold text-red-600">{countdown}s</span> for security.
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleStayLoggedIn}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Stay Logged In
          </button>
          <button
            onClick={doLogout}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}
